import { useEffect, useState } from 'react';
import Link from 'next/link';
import dynamic from 'next/dynamic';
import Head from 'components/Header/Head';
import Header from 'components/Header/Header';
import ArticleNav from 'components/Post/Article/ArticleNav';
import ArticleBody from 'components/Post/Article/ArticleBody';
import RouteFlourish from 'components/Post/Article/RouteFlourish';
import Footer from 'components/General/Footer';
import { getUser } from 'helpers/token';
import Image from 'components/FutureImage';
import { fraunces } from 'components/Landing/fonts';
import {
  ArrowIcon,
  CtaButton,
  Eyebrow,
  GhostButton,
  INK,
  MUTED,
  PAPER_CARD,
} from 'components/Landing/ui';
import cover from '../../public/img/family-blog/wall-map.jpg';
import readingShot from '../../public/img/family-blog/family-reading.jpg';
import articleShot from '../../public/img/blog-platforms/travelfeed-article.jpg';

// Signup CTAs open the boarding-pass JoinDialog (via LoginButton), never
// the legacy /join page; lazy so the dialog stack stays out of the bundle.
const LoginButton = dynamic(
  () => import('components/Header/LoginButton'),
  { ssr: false },
);

const CANONICAL = 'https://travelfeed.com/family-travel-blog';
const COVER_URL = 'https://travelfeed.com/img/family-blog/wall-map.jpg';

// Rendered as visible FAQ AND FAQPage JSON-LD — keep both from one array.
// Deliberately distinct questions from the friends-and-family guide's FAQ
// (that page keeps account-free reading / cost / tech skills).
const FAQ = [
  {
    questionName: 'What is a family travel blog?',
    acceptedAnswerText:
      "A regular blog with a specific audience: the trip lives at one web address, newest post on top, each post pairing the photos with the words that explain them, written for the people who love you rather than for strangers. The family opens a link in any browser; there's no app to install, no account to create, no feed deciding who sees the waterfall. Many later grow a wider readership, but they work perfectly with an audience of twelve.",
  },
  {
    questionName: 'Family travel blog or shared photo album — which is better?',
    acceptedAnswerText:
      'They solve different problems. A shared album is the better dumping ground: every photo, instantly, in one place. A blog is the better story: the twelve photos that mattered, in order, with the context — which country the waterfall was in, why the train was six hours late. Families who try both usually land on the album for completeness and the blog for actually following the trip. If you only keep one, keep the one people will still enjoy opening in ten years.',
  },
  {
    questionName: 'Is a family travel blog private?',
    acceptedAnswerText:
      "A blog is a public website — that openness is exactly why grandparents can open it with one tap, and in practice your readers are the people you send the link to. But 'not advertised' isn't 'private', so apply the family rules: no home address, no live location while traveling, no school names, and consider publishing about a place once you've left it. For genuinely private sharing, a locked photo album or the group chat remains the right tool; the blog is for the version of the trip you're happy to have exist.",
  },
  {
    questionName: 'Can the kids write on a family travel blog?',
    acceptedAnswerText:
      "Yes, and it's quietly one of the best parts. The TravelFeed editor works like writing an email, so a ten-year-old can draft their own version of the day; a family can alternate authors per post, or give each kid a recurring column. It becomes a travel journal the whole crew made together, which is worth more in ten years than any photo dump. One account is all a family needs to start.",
  },
];

function CompareRow({ label, greatAt, failsAt }) {
  return (
    <tr>
      <td className="tw-font-semibold">{label}</td>
      <td>{greatAt}</td>
      <td>{failsAt}</td>
    </tr>
  );
}

// Product lander for "family travel blog creator" intent (253 impr/28d in
// GSC with zero owned page — apple-ads/data/competitor-queries-2026-08-02).
// The how-to guide keeps its query; this page is the product-shaped answer.
export default function FamilyTravelBlog() {
  const [isMember, setIsMember] = useState(false);
  const [joinOpen, setJoinOpen] = useState(false);

  useEffect(() => {
    if (getUser()) setIsMember(true);
  }, []);

  useEffect(() => {
    document.body.classList.add('tf-paper');
    return () => document.body.classList.remove('tf-paper');
  }, []);

  return (
    <>
      <Head
        shorttitle="Family Travel Blog Creator: One Link for the Whole Family"
        description="Create a family travel blog in minutes: photos and stories at one address, readable in any browser with no app, no account, no algorithm. From $19/year on TravelFeed."
        seo={{ canonicalUrl: CANONICAL }}
        image={`https://og.travelfeed.com/api/post?title=${encodeURIComponent(
          'Family Travel Blog Creator: One Link for the Whole Family',
        )}&image=${encodeURIComponent(COVER_URL)}`}
        post={{
          author: { username: 'jpphotography', displayName: 'Julian' },
          createdAt: '2026-08-02',
          updatedAt: '2026-08-02',
          tags: ['travel-blogging'],
          thumbnail: COVER_URL,
        }}
        faq={FAQ}
      />
      {isMember ? <Header /> : <ArticleNav />}
      <div className="tw-pt-20" />

      <header className="tw-mx-auto tw-max-w-[46rem] tw-px-4 tw-pb-4 tw-pt-8 sm:tw-pt-12">
        <Eyebrow>For the whole crew</Eyebrow>
        <h1
          className={`${fraunces.className} tw-m-0 tw-text-4xl tw-font-semibold tw-leading-[1.08] tw-tracking-tight tw-text-[#1D2D28] dark:tw-text-[#EBE6D9] sm:tw-text-5xl`}
        >
          Create a family travel blog everyone can actually follow
        </h1>
        <p className={`${MUTED} tw-mb-0 tw-mt-5 tw-text-lg tw-leading-relaxed`}>
          One link. Every photo next to the words that explain it. Readable by
          a grandparent on any device, with no app to install, no account to
          create, and no algorithm deciding who sees the trip.
        </p>
        <p className="tw-mb-0 tw-mt-5 tw-font-mono tw-text-[11px] tw-uppercase tw-tracking-[0.18em] tw-text-[#8A958F]">
          By{' '}
          <Link
            href="/@jpphotography"
            className="tw-text-primary-700 dark:tw-text-primary-300 hover:tw-underline"
          >
            Julian
          </Link>
          , founder of TravelFeed · Updated August 2026
        </p>
      </header>

      <div className="tw-mx-auto tw-max-w-5xl tw-px-4 tw-pb-6 tw-pt-4">
        <div className="tw-rounded-[4px] tw-bg-white dark:tw-bg-[#EFEAE0] tw-p-3 tw-shadow-[0_30px_60px_-20px_rgba(29,45,40,0.35)]">
          <Image
            src={cover}
            alt="Painted wall map dotted with pins, surrounded by pinned family photos and postcards, a child's drawing tucked into the frame"
            priority
            sizes="(max-width: 1024px) 92vw, 990px"
            className="tw-h-auto tw-w-full tw-rounded-[2px]"
          />
        </div>
      </div>

      <ArticleBody>
        <p>
          Somewhere on every long family trip, the same phone call happens:
          someone&rsquo;s mother asking where exactly you are now, because the
          group chat has become four hundred unlabeled photos and a rumor of a
          waterfall. The fix isn&rsquo;t another app for the family to
          install. It&rsquo;s the oldest trick on the internet: a blog, one
          address where the trip accumulates in order, written by you,
          readable by anyone you hand the link to.
        </p>
        <p>
          This page is for the search that usually ends at a template
          builder: a &ldquo;family travel blog creator&rdquo; that generates a
          pretty one-page site and then leaves you to figure out the actual
          blogging. A template makes a page. What a traveling family needs is
          a <em>habit</em>: somewhere posting from a phone in a hostel lobby
          is easy enough that it actually happens, and where fifty posts
          organize themselves into something the family can browse for years.
          That&rsquo;s the product we build, so the bias here is disclosed
          and the comparison below includes the options that aren&rsquo;t us.
        </p>

        <h2 id="what-a-family-blog-needs">
          What a family travel blog actually needs
        </h2>
        <p>
          The honest requirements list is short and specific:
        </p>
        <ul>
          <li>
            <strong>Zero friction for the readers.</strong> The audience
            includes people who won&rsquo;t install anything and
            shouldn&rsquo;t have to. A browser link that works on a
            ten-year-old iPad is the bar.
          </li>
          <li>
            <strong>Photos with words attached.</strong> The album apps have
            every photo and no story; the story is the reason your aunt keeps
            opening the link.
          </li>
          <li>
            <strong>Order and a map.</strong> &ldquo;Which country was
            that?&rdquo; should be answered by the blog&rsquo;s structure,
            not by scrolling back through March.
          </li>
          <li>
            <strong>Easy enough to keep up mid-trip.</strong> Anything that
            needs a laptop and an evening dies by week three. Posting has to
            work from a phone, tired.
          </li>
          <li>
            <strong>A future.</strong> Group chats scroll away, apps retire,
            and a template site nobody updates is a business card. The trip
            deserves an archive that&rsquo;s still readable in a decade.
          </li>
        </ul>

        <h2 id="the-options">The options families actually try</h2>
        <table>
          <thead>
            <tr>
              <th> </th>
              <th>Great at</th>
              <th>Where it fails the family</th>
            </tr>
          </thead>
          <tbody>
            <CompareRow
              label="The group chat"
              greatAt="Instant, already installed"
              failsAt="Photos land out of order and captionless; mute it for a day and lose the plot"
            />
            <CompareRow
              label="Shared photo album"
              greatAt="Every photo in one place"
              failsAt="No story, no order a reader can follow; a pile, not a trip"
            />
            <CompareRow
              label="Trip-tracker apps"
              greatAt="Beautiful automatic route maps"
              failsAt="An app to install, captions instead of stories, and the archive lives on the app's terms"
            />
            <CompareRow
              label="Website-builder templates"
              greatAt="A pretty page in an hour"
              failsAt="You get a design, not a blogging habit; updating it from the road is nobody's idea of fun"
            />
            <CompareRow
              label="A TravelFeed blog"
              greatAt="The trip at one address: stories, galleries, maps, in order — from $19/year"
              failsAt="It's a public website (see the privacy answer below), and it isn't free"
            />
          </tbody>
        </table>
        <p>
          Yes, we put our own weakness in the right-hand column too. A blog
          is public by nature and $19/year isn&rsquo;t $0. If either is a
          dealbreaker, a locked shared album genuinely is the better tool,
          and we&rsquo;d rather say so than have you find out after signup.
          The tracker-app row also has its own honest deep-dive in{' '}
          <Link href="/polarsteps-alternative">the Polarsteps comparison</Link>
          , and rather than take our word on any of it,{' '}
          <Link href="/blog/travel-blog-examples">four real member blogs</Link>{' '}
          show what the platform produces in the wild.
        </p>

        <h2 id="the-travelfeed-version">
          What the TravelFeed version looks like
        </h2>
        <p>
          Writing a post works like writing an email with much better photo
          handling: blocks for text, galleries and maps, from any phone or
          laptop, no code anywhere. Pin each post to the place it happened
          and the geography takes care of itself: on a hosted family blog,
          country, region and city pages are generated from your posts and
          linked to each other, so &ldquo;show me everything from the Japan
          trip&rdquo; is a page that exists automatically, not an afternoon
          of album curation. On the Starter membership, your pinned posts
          join travelfeed.com&rsquo;s destination pages and your profile
          keeps your own travel map. Either way, the family reads it in the
          browser they already have.
        </p>
        <p>
          This is a real post on a TravelFeed blog, as the family sees it —
          full-width photos, the words alongside, the place on the map:
        </p>
        <figure className="tf-wide">
          <Image
            src={articleShot}
            alt="A live TravelFeed article with a full-width photo masthead, GPS coordinates and editorial typography"
            sizes="(max-width: 640px) 100vw, (max-width: 1088px) 92vw, 1024px"
            className="tw-h-auto tw-w-full"
          />
        </figure>
        <figure className="tf-wide">
          <Image
            src={readingShot}
            alt="A family travel blog open on a tablet at the kitchen table, surrounded by tea, reading glasses and postcards"
            sizes="(max-width: 640px) 100vw, (max-width: 1088px) 92vw, 1024px"
            className="tw-h-auto tw-w-full"
          />
        </figure>
        <p>
          The honest pricing, exactly: the Starter membership is $19 a year
          and publishes your family&rsquo;s posts on the TravelFeed
          community, ad-free, no domain needed. It&rsquo;s the right first
          step for most families. Want it on your own domain instead? Year
          one is $150 on Pro (a 50% new-member discount applies
          automatically at checkout; it renews at $300/yr), or $180 a year
          on Basic, plus $10–15 for the domain itself; the full cost math,
          including every option that isn&rsquo;t us, is in{' '}
          <Link href="/blog/how-much-does-it-cost-to-start-a-travel-blog">
            what a travel blog really costs
          </Link>
          .
        </p>
        <p>
          And when the trip is over, the blog quietly becomes the thing you
          actually made: the family&rsquo;s own archive (the kids&rsquo;
          guest posts included) at an address that doesn&rsquo;t scroll
          away, sitting on the shelf next to the photo books. A blog written
          for twelve readers is a real blog — often a better one, for knowing
          exactly who it&rsquo;s for.
        </p>

        <aside
          className={`${PAPER_CARD} tw-my-10 tw-rounded-2xl tw-p-6 tw-text-center sm:tw-p-8`}
        >
          <p
            className={`${fraunces.className} ${INK} tw-mb-2 tw-mt-0 tw-text-xl tw-font-semibold tw-tracking-tight sm:tw-text-2xl`}
          >
            Start it before the trip does.
          </p>
          <p
            className={`${MUTED} tw-mx-auto tw-mb-5 tw-mt-0 tw-max-w-md tw-text-[0.95em]`}
          >
            Write the first post tonight and the link already works when you
            land. $19 a year, cancel anytime.
          </p>
          <CtaButton onClick={() => setJoinOpen(true)}>
            Create your family travel blog
            <ArrowIcon />
          </CtaButton>
        </aside>

        <h2 id="how-to-start">Starting one this week</h2>
        <p>
          The mechanics take an afternoon: pick the audience (just family,
          or public-with-family-in-mind), set up the blog, send the link to
          the group chat, and write the first post before the trip so the
          address already works when you land. The step-by-step lives in the{' '}
          <Link href="/blog/how-to-make-a-travel-blog-for-friends-and-family">
            full guide to making a travel blog your family will read
          </Link>{' '}
          — including the craft parts, like writing for readers who love you
          but skim. For what to post beyond the trip diary, the letters-home
          format in our{' '}
          <Link href="/blog/travel-blog-post-ideas">post ideas list</Link>{' '}
          was practically invented for family blogs.
        </p>

        <h2 id="faq">Frequently asked questions</h2>
        {FAQ.map(entry => (
          <div key={entry.questionName}>
            <h3>{entry.questionName}</h3>
            <p>{entry.acceptedAnswerText}</p>
          </div>
        ))}
      </ArticleBody>

      <section className="tw-mx-auto tw-max-w-[46rem] tw-px-4 tw-pb-16 tw-pt-8">
        <RouteFlourish className="tw-pb-10" />
        <div
          className={`${PAPER_CARD} tw-rounded-2xl tw-p-7 tw-text-center sm:tw-p-10`}
        >
          <p
            className={`${fraunces.className} ${INK} tw-mb-3 tw-mt-0 tw-text-2xl tw-font-semibold tw-tracking-tight sm:tw-text-3xl`}
          >
            Give the family one link to follow.
          </p>
          <p className={`${MUTED} tw-mx-auto tw-mb-7 tw-mt-0 tw-max-w-lg`}>
            Photos with their stories, maps that answer &ldquo;where are you
            now?&rdquo;, readable on any device. Live before the next trip
            starts, from $19 a year.
          </p>
          <div className="tw-flex tw-flex-wrap tw-items-center tw-justify-center tw-gap-3">
            <CtaButton onClick={() => setJoinOpen(true)}>
              Create your family travel blog
              <ArrowIcon />
            </CtaButton>
            <GhostButton href="/blog/how-to-make-a-travel-blog-for-friends-and-family">
              Read the family guide first
            </GhostButton>
          </div>
        </div>
      </section>

      {joinOpen && (
        <LoginButton
          open
          hideButtons
          onClickClose={() => setJoinOpen(false)}
        />
      )}
      <Footer isNoSpace />
    </>
  );
}
